import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import Toast from '@/components/Toast';
import LoadingOverlay from '@/components/LoadingOverlay';
import { ArticleConfigModals } from '@/components/ArticleConfigModals';
import {
  ArticleCreateFormFields,
  type ArticleCreateFormFieldsValue,
} from '@/components/ArticleCreateFormFields';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { adminApi } from '@/features/admin/components/adminApi';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import { Article } from '@/types/article';
import {
  configureQueryKeys,
  useAdminCategoriesQuery,
  useAdminCountriesQuery,
  useAdminTopicsQuery,
} from '@hooks/useConfigureQueries';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface CreateArticleForm {
  country: string;
  category: string;
  topicCode: string; // topic code
  tags: string[]; // optional tags (multi-select)
  articleTopic: string; // article title/topic text
  description: string;
  articleType: ArticleCreateFormFieldsValue['articleType'];
}

interface ArpproveArticleForm {
  articleId?: string;
  createdAt?: string;
  publishedAt?: string;
  summaryReviewedBy?: string;
  auLevelA: string;
  auLevelB: string;
  auLevelC: string;
  ukLevelA: string;
  ukLevelB: string;
  ukLevelC: string;
  usLevelA: string;
  usLevelB: string;
  usLevelC: string;
}

interface KeyTakeawayForm {
  keyTakeawayALevelVI: string;
  keyTakeawayBLevelVI: string;
  keyTakeawayCLevelVI: string;
}

/**
 * Create Article Component
 * Allows users to create a new article
 * Allows selecting category for the article
 * Allows entering article topic and description
 */
export const ArticleCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [addCountryForm, setAddCountryForm] = useState({
    countryCode: '',
    countryNameEN: '',
    countryNameVI: '',
  });
  const [addCategoryForm, setAddCategoryForm] = useState({
    categoryCode: '',
    categoryNameEN: '',
    categoryNameVI: '',
  });
  const [addTopicForm, setAddTopicForm] = useState({
    topicCode: '',
    topicNameEN: '',
    topicNameVI: '',
  });
  const [addTagForm, setAddTagForm] = useState({
    tag: '',
  });
  const { data: countries = [] } = useAdminCountriesQuery();
  const { data: categories = [] } = useAdminCategoriesQuery();
  const [createdArticle, setCreatedArticle] = useState<{
    articleId?: string;
    articleType?: 'monologue' | 'dialogue';
    title?: string;
    titleAU?: string;
    titleUK?: string;
    titleUS?: string;
    category?: string;
    createdAt?: string;
    keyTakeawayALevelVI?: string;
    keyTakeawayBLevelVI?: string;
    keyTakeawayCLevelVI?: string;
  } | null>(null);
  const [createForm, setCreateForm] = useState<CreateArticleForm>({
    country: '',
    category: '',
    topicCode: '',
    tags: [],
    articleTopic: '',
    description: '',
    articleType: '',
  });
  const { data: topics = [] } = useAdminTopicsQuery();
  const [tags, setTags] = useState<Array<{ tag: string }>>([]);
  const [approveForm, setApproveForm] = useState<ArpproveArticleForm>({
    auLevelA: '',
    auLevelB: '',
    auLevelC: '',
    ukLevelA: '',
    ukLevelB: '',
    ukLevelC: '',
    usLevelA: '',
    usLevelB: '',
    usLevelC: '',
  });
  const [keyTakeawayForm, setKeyTakeawayForm] = useState<KeyTakeawayForm>({
    keyTakeawayALevelVI: '',
    keyTakeawayBLevelVI: '',
    keyTakeawayCLevelVI: '',
  });
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const notifTimeout = useRef<number | null>(null);
  const [groupedBy, setGroupedBy] = useState<'country' | 'topic'>('country');

  const getSelectedContentByLevel = (level: 'A' | 'B' | 'C'): string => {
    const selectedCountry = (createForm.country || '').toUpperCase();

    // Topic and VI use US variant fields.
    if (groupedBy === 'topic' || selectedCountry === 'VI') {
      if (level === 'A') return approveForm.usLevelA;
      if (level === 'B') return approveForm.usLevelB;
      return approveForm.usLevelC;
    }

    if (selectedCountry === 'AU') {
      if (level === 'A') return approveForm.auLevelA;
      if (level === 'B') return approveForm.auLevelB;
      return approveForm.auLevelC;
    }

    if (selectedCountry === 'UK') {
      if (level === 'A') return approveForm.ukLevelA;
      if (level === 'B') return approveForm.ukLevelB;
      return approveForm.ukLevelC;
    }

    // US country stores content in EN keys in DB but uses US editors on this screen.
    if (selectedCountry === 'US') {
      if (level === 'A') return approveForm.usLevelA;
      if (level === 'B') return approveForm.usLevelB;
      return approveForm.usLevelC;
    }

    if (level === 'A') return approveForm.auLevelA || approveForm.ukLevelA || approveForm.usLevelA;
    if (level === 'B') return approveForm.auLevelB || approveForm.ukLevelB || approveForm.usLevelB;
    return approveForm.auLevelC || approveForm.ukLevelC || approveForm.usLevelC;
  };

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  const getGeneratedPlaceholder = (
    variantName: 'Australian' | 'UK' | 'US',
    level: 'A' | 'B' | 'C'
  ): string => {
    return `Generated ${variantName} English ${getLevelLabel(level)} content will be displayed here`;
  };

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    };
  }, []);

  // Fetch tags from API
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const data = await adminApi.getAllTags();
        setTags(data || []);
      } catch (error) {
        showNotification('Failed to load tags', 'error');
      }
    };
    fetchTags();
  }, []);

  const handleCreateFormFieldChange = (
    field: keyof ArticleCreateFormFieldsValue,
    value: string | string[]
  ): void => {
    if (field === 'groupedBy') {
      setGroupedBy(value as 'country' | 'topic');
      return;
    }

    setCreateForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApproveChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setApproveForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildApproveForm = (countryId: string, data: any): ArpproveArticleForm => {
    const blank: ArpproveArticleForm = {
      articleId: '',
      createdAt: '',
      auLevelA: '',
      auLevelB: '',
      auLevelC: '',
      ukLevelA: '',
      ukLevelB: '',
      ukLevelC: '',
      usLevelA: '',
      usLevelB: '',
      usLevelC: '',
    };
    const c = (countryId || '').toUpperCase();
    if (!c || c === 'VI') {
      return {
        auLevelA: data?.textALevelAU || '',
        auLevelB: data?.textBLevelAU || '',
        auLevelC: data?.textCLevelAU || '',
        ukLevelA: data?.textALevelUK || '',
        ukLevelB: data?.textBLevelUK || '',
        ukLevelC: data?.textCLevelUK || '',
        usLevelA: data?.textALevelUS || '',
        usLevelB: data?.textBLevelUS || '',
        usLevelC: data?.textCLevelUS || '',
        articleId: data?.articleId || '',
        createdAt: data?.createdAt || '',
      };
    }
    if (c === 'AU') {
      return {
        ...blank,
        auLevelA: data?.textALevelEN || '',
        auLevelB: data?.textBLevelEN || '',
        auLevelC: data?.textCLevelEN || '',
      };
    }
    if (c === 'UK') {
      return {
        ...blank,
        ukLevelA: data?.textALevelEN || '',
        ukLevelB: data?.textBLevelEN || '',
        ukLevelC: data?.textCLevelEN || '',
      };
    }
    if (c === 'US') {
      return {
        ...blank,
        usLevelA: data?.textALevelEN || '',
        usLevelB: data?.textBLevelEN || '',
        usLevelC: data?.textCLevelEN || '',
      };
    }
    return blank;
  };

  const handleAddCountry = async () => {
    const newCountryCode = addCountryForm.countryCode.trim();

    if (
      !newCountryCode ||
      !addCountryForm.countryNameEN.trim() ||
      !addCountryForm.countryNameVI.trim()
    ) {
      showNotification('Please fill in all country fields', 'error');
      return;
    }
    setIsLoading(true);
    try {
      await adminApi.addCountry(addCountryForm);
      showNotification('Country added successfully', 'success');
      setGroupedBy('country');
      setCreateForm(prev => ({
        ...prev,
        country: newCountryCode,
      }));
      setShowAddCountryModal(false);
      setAddCountryForm({ countryCode: '', countryNameEN: '', countryNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminCountries });
    } catch (error) {
      showNotification('Failed to add country', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const newCategoryCode = addCategoryForm.categoryCode.trim();

    if (
      !newCategoryCode ||
      !addCategoryForm.categoryNameEN.trim() ||
      !addCategoryForm.categoryNameVI.trim()
    ) {
      showNotification('Please fill in all category fields', 'error');
      return;
    }
    setIsLoading(true);
    try {
      await adminApi.addCategory(addCategoryForm);
      showNotification('Category added successfully', 'success');
      setGroupedBy('country');
      setCreateForm(prev => ({
        ...prev,
        category: newCategoryCode,
      }));
      setShowAddCategoryModal(false);
      setAddCategoryForm({ categoryCode: '', categoryNameEN: '', categoryNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminCategories });
    } catch (error) {
      showNotification('Failed to add category', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTopic = async () => {
    if (
      !addTopicForm.topicCode.trim() ||
      !addTopicForm.topicNameEN.trim() ||
      !addTopicForm.topicNameVI.trim()
    ) {
      showNotification('Please fill in all topic fields', 'error');
      return;
    }
    setIsLoading(true);
    try {
      await adminApi.addTopic(addTopicForm);
      showNotification('Topic added successfully', 'success');
      setShowAddTopicModal(false);
      setAddTopicForm({ topicCode: '', topicNameEN: '', topicNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminTopics });
    } catch (error) {
      showNotification('Failed to add topic', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = async () => {
    const newTag = addTagForm.tag.trim();

    if (!newTag) {
      showNotification('Please fill in all tag fields', 'error');
      return;
    }
    setIsLoading(true);
    try {
      await adminApi.addTag(addTagForm);
      showNotification('Tag added successfully', 'success');
      setCreateForm(prev => ({
        ...prev,
        tags: prev.tags.includes(newTag) ? prev.tags : [...prev.tags, newTag],
      }));
      setShowAddTagModal(false);
      setAddTagForm({ tag: '' });
      // Refresh tags list
      const data = await adminApi.getAllTags();
      setTags(data || []);
    } catch (error) {
      showNotification('Failed to add tag', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateArticle = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation depending on Create By mode
    if (groupedBy === 'country') {
      if (!createForm.country) {
        showNotification('Please select a country', 'error');
        return;
      }
      if (!createForm.category) {
        showNotification('Please select a category', 'error');
        return;
      }
    } else {
      // by topic
      if (!createForm.topicCode) {
        showNotification('Please select a topic', 'error');
        return;
      }
    }

    if (!createForm.articleTopic.trim()) {
      showNotification('Please enter an article topic', 'error');
      return;
    }
    if (!createForm.description.trim()) {
      showNotification('Please enter an article description', 'error');
      return;
    }

    if (!createForm.articleType) {
      showNotification('Please select article type', 'error');
      return;
    }

    setIsLoading(true);
    try {
      let articleData: any;
      if (groupedBy === 'country') {
        articleData = {
          country: createForm.country,
          category: createForm.category,
          tags: createForm.tags,
          articleTopic: createForm.articleTopic,
          description: createForm.description,
          groupedBy: 'country',
          articleType: createForm.articleType,
          createdBy: user?.name || 'Unknown',
        };
      } else {
        articleData = {
          topicCode: createForm.topicCode,
          tags: createForm.tags,
          articleTopic: createForm.articleTopic,
          description: createForm.description,
          groupedBy: 'topic',
          articleType: createForm.articleType,
          createdBy: user?.name || 'Unknown',
        };
      }

      const result = await articleAdminApi.create(articleData);
      // Result may be { success, data } or raw item; normalise here
      const data = (result && (result.data || result)) as any;

      // Persist created article in local state to show in UI
      setCreatedArticle({
        articleId: data?.articleId,
        articleType: articleData?.articleType,
        title: data?.title,
        titleAU: data?.titleAU,
        titleUK: data?.titleUK,
        titleUS: data?.titleUS,
        category: data?.category,
        createdAt: data?.createdAt,
        keyTakeawayALevelVI: data?.keyTakeawayALevelVI,
        keyTakeawayBLevelVI: data?.keyTakeawayBLevelVI,
        keyTakeawayCLevelVI: data?.keyTakeawayCLevelVI,
      });

      setKeyTakeawayForm({
        keyTakeawayALevelVI: data?.keyTakeawayALevelVI || '',
        keyTakeawayBLevelVI: data?.keyTakeawayBLevelVI || '',
        keyTakeawayCLevelVI: data?.keyTakeawayCLevelVI || '',
      });

      // Pre-fill approval form based on selected country using reusable helper
      const selectedCountry = (createForm.country || '').toLocaleLowerCase();
      const approveFormData = buildApproveForm(selectedCountry, data);
      setApproveForm({
        ...approveFormData,
        articleId: data?.articleId,
        createdAt: data?.createdAt,
        publishedAt: data?.publishedAt,
      });

      showNotification('Article generated and saved successfully!', 'success');
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.message || error.message
          : 'Failed to create article. Please try again.';
      showNotification(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (validateApproveForm()) {
      setIsLoading(true);
      try {
        const isUSText = createForm.country.toUpperCase() === 'VI' || groupedBy === 'topic';
        const textALevelKey = isUSText ? 'textALevelUS' : 'textALevelEN';
        const textBLevelKey = isUSText ? 'textBLevelUS' : 'textBLevelEN';
        const textCLevelKey = isUSText ? 'textCLevelUS' : 'textCLevelEN';
        const updateData: Article = {
          articleId: approveForm.articleId || '',
          [textALevelKey]: getSelectedContentByLevel('A'),
          [textBLevelKey]: getSelectedContentByLevel('B'),
          [textCLevelKey]: getSelectedContentByLevel('C'),
          createdAt: approveForm.createdAt || '',
          publishedAt: approveForm.publishedAt || '',
          articleType: createdArticle?.articleType || 'monologue',
          tags: createForm.tags,
          keyTakeawayALevelVI: keyTakeawayForm.keyTakeawayALevelVI,
          keyTakeawayBLevelVI: keyTakeawayForm.keyTakeawayBLevelVI,
          keyTakeawayCLevelVI: keyTakeawayForm.keyTakeawayCLevelVI,
        };
        await articleAdminApi.update(createForm.country!, approveForm.articleId!, updateData);
        setApproveForm(() => ({
          articleId: '',
          createdAt: '',
          publishedAt: '',
          auLevelA: '',
          auLevelB: '',
          auLevelC: '',
          ukLevelA: '',
          ukLevelB: '',
          ukLevelC: '',
          usLevelA: '',
          usLevelB: '',
          usLevelC: '',
        }));
        showNotification('Article saved successfully', 'success');
      } catch (error) {
        showNotification('Failed to save article', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  function validateApproveForm(): boolean {
    const selectedCountry = (createForm.country || '').toUpperCase();
    const needAU = selectedCountry === 'AU';
    const needUK = selectedCountry === 'UK';
    const needUS = selectedCountry === 'VI' || selectedCountry === 'US';

    const missingAU =
      needAU &&
      (!approveForm.auLevelA.trim() ||
        !approveForm.auLevelB.trim() ||
        !approveForm.auLevelC.trim());
    const missingUK =
      needUK &&
      (!approveForm.ukLevelA.trim() ||
        !approveForm.ukLevelB.trim() ||
        !approveForm.ukLevelC.trim());
    const missingUS =
      needUS &&
      (!approveForm.usLevelA.trim() ||
        !approveForm.usLevelB.trim() ||
        !approveForm.usLevelC.trim());

    if (missingAU || missingUK || missingUS) {
      showNotification('Please fill in the required approval levels', 'error');
      return false;
    }

    return true;
  }

  const handleApproveArticle = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (validateApproveForm()) {
      setShowApproveConfirm(true);
    }
  };

  const performApprove = async () => {
    if (!createdArticle?.articleId) return;
    // Close the confirm modal right away so the UI isn't blocked by it
    setShowApproveConfirm(false);
    setIsLoading(true);
    try {
      // call backend approve endpoint which will update article, extract vocab, generate tts and translations
      const isUSText = createForm.country.toUpperCase() === 'VI' || groupedBy === 'topic';
      const textALevelKey = isUSText ? 'textALevelUS' : 'textALevelEN';
      const textBLevelKey = isUSText ? 'textBLevelUS' : 'textBLevelEN';
      const textCLevelKey = isUSText ? 'textCLevelUS' : 'textCLevelEN';
      const approveData: Article = {
        articleId: approveForm.articleId || '',
        articleType: createdArticle?.articleType || 'monologue',
        [textALevelKey]: getSelectedContentByLevel('A'),
        [textBLevelKey]: getSelectedContentByLevel('B'),
        [textCLevelKey]: getSelectedContentByLevel('C'),
        createdAt: approveForm.createdAt || '',
        summaryReviewedBy: user?.name || 'Unknown',
        publishedAt: approveForm.publishedAt || '',
        tags: createForm.tags,
        keyTakeawayALevelVI: keyTakeawayForm.keyTakeawayALevelVI,
        keyTakeawayBLevelVI: keyTakeawayForm.keyTakeawayBLevelVI,
        keyTakeawayCLevelVI: keyTakeawayForm.keyTakeawayCLevelVI,
      };

      articleAdminApi.approveSummary(
        createForm.country || '',
        createdArticle.articleId,
        approveData
      );
      setApproveForm(() => ({
        articleId: '',
        createdAt: '',
        publishedAt: '',
        auLevelA: '',
        auLevelB: '',
        auLevelC: '',
        ukLevelA: '',
        ukLevelB: '',
        ukLevelC: '',
        usLevelA: '',
        usLevelB: '',
        usLevelC: '',
      }));
      setTimeout(() => {
        showNotification('Article approved', 'success');
      }, 2000);
    } catch (err) {
      showNotification('Failed to approve article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {isLoading && <LoadingOverlay />}
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showApproveConfirm}
        title="Confirm Approve Article"
        description="Are you sure you want to approve this article?"
        confirmText="Yes, approve"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />

      <ArticleConfigModals
        idPrefix="single-create-modal"
        isLoading={isLoading}
        showAddCountryModal={showAddCountryModal}
        showAddCategoryModal={showAddCategoryModal}
        showAddTopicModal={showAddTopicModal}
        showAddTagModal={showAddTagModal}
        addCountryForm={addCountryForm}
        addCategoryForm={addCategoryForm}
        addTopicForm={addTopicForm}
        addTagForm={addTagForm}
        setAddCountryForm={setAddCountryForm}
        setAddCategoryForm={setAddCategoryForm}
        setAddTopicForm={setAddTopicForm}
        setAddTagForm={setAddTagForm}
        onCloseAddCountryModal={() => {
          setShowAddCountryModal(false);
          setAddCountryForm({ countryCode: '', countryNameEN: '', countryNameVI: '' });
        }}
        onCloseAddCategoryModal={() => {
          setShowAddCategoryModal(false);
          setAddCategoryForm({ categoryCode: '', categoryNameEN: '', categoryNameVI: '' });
        }}
        onCloseAddTopicModal={() => {
          setShowAddTopicModal(false);
          setAddTopicForm({ topicCode: '', topicNameEN: '', topicNameVI: '' });
        }}
        onCloseAddTagModal={() => {
          setShowAddTagModal(false);
          setAddTagForm({ tag: '' });
        }}
        onSubmitAddCountry={handleAddCountry}
        onSubmitAddCategory={handleAddCategory}
        onSubmitAddTopic={handleAddTopic}
        onSubmitAddTag={handleAddTag}
      />

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Create New Article</h1>
        <p className="text-gray-600 mt-2">Enter article details to get started</p>
      </div>

      <Card className="max-w-3xl">
        <form onSubmit={handleCreateArticle} className="space-y-6">
          <ArticleCreateFormFields
            idPrefix="single-create"
            value={{
              groupedBy,
              country: createForm.country,
              category: createForm.category,
              topicCode: createForm.topicCode,
              tags: createForm.tags,
              articleTopic: createForm.articleTopic,
              description: createForm.description,
              articleType: createForm.articleType,
            }}
            countries={countries}
            categories={categories}
            topics={topics}
            tags={tags}
            isAdmin={user?.role === 'admin'}
            onChangeField={handleCreateFormFieldChange}
            onOpenAddCountry={() => setShowAddCountryModal(true)}
            onOpenAddCategory={() => setShowAddCategoryModal(true)}
            onOpenAddTopic={() => setShowAddTopicModal(true)}
            onOpenAddTag={() => setShowAddTagModal(true)}
            descriptionLabel="Situation"
            descriptionPlaceholder="Enter article content or description"
            descriptionRows={8}
            showDescriptionHelp={true}
          />

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
              Create Article
            </Button>
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                Cancel
              </Button>
            )}
          </div>
        </form>

        {/* Generated Result Preview */}
        {createdArticle && approveForm?.articleId && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Result</h3>
            <div className="space-y-3 text-sm text-gray-700">
              {createdArticle.articleId && (
                <p>
                  <span className="font-medium">Article ID:</span> {createdArticle.articleId}
                </p>
              )}
              {createdArticle.title && !createdArticle.titleAU && (
                <p>
                  <span className="font-medium">Title:</span> {createdArticle.title}
                </p>
              )}
              {createdArticle.titleAU && (
                <p>
                  <span className="font-medium">Title (AU):</span> {createdArticle.titleAU}
                </p>
              )}
              {createdArticle.titleUK && (
                <p>
                  <span className="font-medium">Title (UK):</span> {createdArticle.titleUK}
                </p>
              )}
              {createdArticle.titleUS && (
                <p>
                  <span className="font-medium">Title (US):</span> {createdArticle.titleUS}
                </p>
              )}
              {createdArticle.category && (
                <p>
                  <span className="font-medium">Category:</span> {createdArticle.category}
                </p>
              )}
              {createdArticle.createdAt && (
                <p>
                  <span className="font-medium">Created At:</span>{' '}
                  {new Date(createdArticle.createdAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleApproveArticle} className="space-y-6">
          {/* Generated Contents - Three Row Layout */}
          {approveForm?.articleId && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Contents</h3>
              <div className="grid grid-cols-1 gap-6">
                {/* Row 1: Level A */}
                <div className="space-y-4">
                  {(createForm.country || '').toUpperCase() === 'AU' && (
                    <div>
                      <label
                        htmlFor="auLevelA"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        AU {getLevelLabel('A')}
                      </label>
                      <textarea
                        id="auLevelA"
                        name="auLevelA"
                        value={approveForm.auLevelA}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('Australian', 'A')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {(createForm.country || '').toUpperCase() === 'UK' && (
                    <div>
                      <label
                        htmlFor="ukLevelA"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        UK {getLevelLabel('A')}
                      </label>
                      <textarea
                        id="ukLevelA"
                        name="ukLevelA"
                        value={approveForm.ukLevelA}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('UK', 'A')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {((createForm.country || '').toUpperCase() === 'VI' ||
                    (createForm.country || '').toUpperCase() === 'US' ||
                    groupedBy === 'topic') && (
                    <div>
                      <label
                        htmlFor="usLevelA"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        US {getLevelLabel('A')}
                      </label>
                      <textarea
                        id="usLevelA"
                        name="usLevelA"
                        value={approveForm.usLevelA}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('US', 'A')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}
                </div>

                {/* Row 2: Level B */}
                <div className="space-y-4">
                  {(createForm.country || '').toUpperCase() === 'AU' && (
                    <div>
                      <label
                        htmlFor="auLevelB"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        AU {getLevelLabel('B')}
                      </label>
                      <textarea
                        id="auLevelB"
                        name="auLevelB"
                        value={approveForm.auLevelB}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('Australian', 'B')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {(createForm.country || '').toUpperCase() === 'UK' && (
                    <div>
                      <label
                        htmlFor="ukLevelB"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        UK {getLevelLabel('B')}
                      </label>
                      <textarea
                        id="ukLevelB"
                        name="ukLevelB"
                        value={approveForm.ukLevelB}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('UK', 'B')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {((createForm.country || '').toUpperCase() === 'VI' ||
                    (createForm.country || '').toUpperCase() === 'US' ||
                    groupedBy === 'topic') && (
                    <div>
                      <label
                        htmlFor="usLevelB"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        US {getLevelLabel('B')}
                      </label>
                      <textarea
                        id="usLevelB"
                        name="usLevelB"
                        value={approveForm.usLevelB}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('US', 'B')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}
                </div>

                {/* Row 3: Level C */}
                <div className="space-y-4">
                  {(createForm.country || '').toUpperCase() === 'AU' && (
                    <div>
                      <label
                        htmlFor="auLevelC"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        AU {getLevelLabel('C')}
                      </label>
                      <textarea
                        id="auLevelC"
                        name="auLevelC"
                        value={approveForm.auLevelC}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('Australian', 'C')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {(createForm.country || '').toUpperCase() === 'UK' && (
                    <div>
                      <label
                        htmlFor="ukLevelC"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        UK {getLevelLabel('C')}
                      </label>
                      <textarea
                        id="ukLevelC"
                        name="ukLevelC"
                        value={approveForm.ukLevelC}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('UK', 'C')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}

                  {((createForm.country || '').toUpperCase() === 'VI' ||
                    (createForm.country || '').toUpperCase() === 'US' ||
                    groupedBy === 'topic') && (
                    <div>
                      <label
                        htmlFor="usLevelC"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        US {getLevelLabel('C')}
                      </label>
                      <textarea
                        id="usLevelC"
                        name="usLevelC"
                        value={approveForm.usLevelC}
                        onChange={handleApproveChange}
                        placeholder={getGeneratedPlaceholder('US', 'C')}
                        rows={7}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Approve Form Action Buttons */}
          <div className="flex gap-4 pt-4">
            {/* Only display Save Draft button if summary is not reviewed */}
            {approveForm?.articleId && (
              <Button type="button" onClick={handleSave} disabled={isLoading} className="flex-1">
                {isLoading ? 'Saving...' : 'Save Draft'}
              </Button>
            )}

            {approveForm?.articleId && (
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Approve Article
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};
